/**
 * Notificaciones del usuario autenticado (`/api/notificaciones`).
 *
 * Cada usuario gestiona solo las suyas; no hay permiso adicional que lo controle,
 * el backend las escopa por usuario.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

// RF-13 · Notificaciones del usuario (campana). Cada quien ve solo las suyas.

export interface Notificacion {
  id_notificacion: number
  tipo: 'recordatorio' | 'alerta'
  titulo: string
  mensaje: string | null
  enlace: string | null
  severidad: 'critica' | 'alta' | 'media' | 'informativa'
  leida_en: string | null
  created_at: string
}

/** Forma con la que el API de notificacion devuelve este dato. */
export interface PaginaNotificaciones {
  data: Notificacion[]
  total: number
  current_page: number
  last_page: number
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Consulta notificacion · `GET /api/notificaciones?page=`.
 *
 * @returns Resultado<PaginaNotificaciones>
 */
export async function getNotificacionesService(pagina = 1): Promise<Resultado<PaginaNotificaciones>> {
  const r = await http.get<PaginaNotificaciones>(`/api/notificaciones?page=${pagina}`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudieron cargar las notificaciones') }
}

/**
 * Consulta notificacion · `GET /api/notificaciones/contador`.
 *
 * @returns number
 */
export async function getContadorNotificacionesService(): Promise<number> {
  const r = await http.get<{ no_leidas: number }>('/api/notificaciones/contador')
  return r.ok ? r.data?.no_leidas ?? 0 : 0
}

/**
 * Operación sobre notificacion · `PATCH /api/notificaciones`.
 *
 * @returns void
 */
export async function marcarLeidaService(id: number): Promise<void> {
  await http.patch(`/api/notificaciones/${id}/leer`)
}

/**
 * Operación sobre notificacion · `POST /api/notificaciones/leer-todas`.
 *
 * @returns void
 */
export async function marcarTodasLeidasService(): Promise<void> {
  await http.post('/api/notificaciones/leer-todas')
}
